import React, { useState } from 'react';
import { Search, ChevronDown, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';

/**
 * Searchable dropdown replacement for Select.
 * Props:
 *   value: string
 *   onValueChange: (val: string) => void
 *   onChange?: (val: string) => void
 *   options: Array<{ value: string, label: string, sublabel?: string }>
 *   placeholder?: string
 *   searchPlaceholder?: string
 *   className?: string
 *   popoverClassName?: string
 *   disabled?: boolean
 */
export default function SearchableSelect({
  value,
  onValueChange,
  onChange,
  options = [],
  placeholder = 'Pilih...',
  searchPlaceholder = 'Cari...',
  className,
  popoverClassName,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const listRef = React.useRef(null);

  const handleCallback = onValueChange || onChange || (() => {});

  const selected = options.find((o) => String(o.value) === String(value));
  const filtered = query
    ? options.filter((o) => {
        const q = String(query).toLowerCase().trim();
        const lMatch = String(o.label || '').toLowerCase().includes(q);
        const subMatch = String(o.sublabel || '').toLowerCase().includes(q);
        const valMatch = String(o.value || '').toLowerCase().includes(q);
        return lMatch || subMatch || valMatch;
      })
    : options;

  const handleSelect = (val) => {
    handleCallback(val);
    setOpen(false);
    setQuery('');
  };

  // Memastikan scroll dengan mouse wheel / trackpad selalu bekerja lancar bahkan di dalam Dialog (mengatasi react-remove-scroll block)
  React.useEffect(() => {
    const el = listRef.current;
    if (!el || !open) return;

    const handleWheel = (e) => {
      e.stopPropagation();
      const delta = e.deltaMode === 1 ? e.deltaY * 24 : e.deltaY;
      el.scrollTop += delta;
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [open, filtered.length]);

  return (
    <Popover
      open={open}
      onOpenChange={(isOpen) => {
        if (disabled) return;
        setOpen(isOpen);
        if (!isOpen) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'flex h-10 w-full items-center justify-between whitespace-nowrap rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm transition-all focus:outline-none focus:ring-1 focus:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer text-left',
            className
          )}
        >
          <span className={cn('truncate', !selected && 'text-slate-400 font-normal')}>
            {selected ? (
              <span className="flex items-center gap-1.5 truncate">
                <span className="truncate">{selected.label}</span>
                {selected.sublabel && (
                  <span className="text-[11px] text-slate-400 font-normal shrink-0">
                    ({selected.sublabel})
                  </span>
                )}
              </span>
            ) : (
              placeholder
            )}
          </span>
          <ChevronDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        className={cn(
          'w-[var(--radix-popover-trigger-width)] min-w-[280px] p-0 z-[10050] bg-white border border-slate-200 shadow-xl rounded-xl overflow-hidden',
          popoverClassName
        )}
        align="start"
      >
        <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2 bg-slate-50/50">
          <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className="flex-1 bg-transparent text-xs outline-none placeholder:text-slate-400"
          />
          {query ? (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="text-slate-400 hover:text-slate-600 p-0.5 rounded cursor-pointer"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          ) : (
            <span className="text-[10px] text-slate-400 shrink-0">
              {options.length} item
            </span>
          )}
        </div>
        <div 
          ref={listRef}
          onWheel={(e) => {
            e.stopPropagation();
            const delta = e.deltaMode === 1 ? e.deltaY * 24 : e.deltaY;
            e.currentTarget.scrollTop += delta;
          }}
          style={{ overscrollBehavior: 'contain' }}
          className="max-h-72 overflow-y-auto p-1 divide-y divide-slate-50"
        >
          {filtered.length === 0 ? (
            <p className="text-center text-xs text-slate-400 py-5">
              Tidak ada hasil yang cocok dengan &quot;{query}&quot;
            </p>
          ) : (
            filtered.map((opt) => {
              const isSelected = String(value) === String(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleSelect(opt.value)}
                  className={cn(
                    'flex w-full items-start gap-2 rounded-lg px-2.5 py-2 text-xs text-left transition-colors cursor-pointer',
                    isSelected
                      ? 'bg-[#FAF7F2] text-[#8C6208] font-bold'
                      : 'hover:bg-slate-50 text-slate-700'
                  )}
                >
                  <Check
                    className={cn(
                      'w-3.5 h-3.5 shrink-0 mt-0.5 text-[#8C6208]',
                      isSelected ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="truncate leading-tight">{opt.label}</span>
                    {opt.sublabel && (
                      <span className="text-[10px] text-slate-400 font-normal truncate mt-0.5">
                        {opt.sublabel}
                      </span>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}